import {dispatcher} from '@netx/core-next/instance/dispatcher';
import Netx from '@netx/core/bootstrap';
import {URL} from '@netx/core-next/url';
import {isSafari} from '@netx/core-next/utils/browser/isSafari';
import {getCurrentPortalProp} from '@netx/core-cms-next/utils/getCurrentPortalProp';
import type {PaletteMode} from '@mui/material';
import {defaultMuiTheme} from '../defaults';
import type {WatermarkPosition} from '../types';
import {getStylePropertyFn} from './getStylePropertyFn';
import type {CSSProperties} from '@mui/styles/withStyles';

let watermarkCacheBuster = Date.now();
dispatcher.on('asset:watermark:update', () => {
	watermarkCacheBuster = Date.now();
});

const buildCSSProperties = (props: CSSProperties): CSSProperties => props;

export const getThemeMixins = (mode: PaletteMode, opts?: {demo?: boolean}) => {
	const getStyleProperty = getStylePropertyFn(mode, opts);

	return {
		absoluteFill: buildCSSProperties({
			position: 'absolute',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
		}),
		disabled: buildCSSProperties({
			opacity: 0.5,
		}),
		flexFillColumn: buildCSSProperties({
			display: 'flex',
			flex: '1 1 auto',
			flexDirection: 'column',
			minHeight: 0,
		}),
		formFooter: buildCSSProperties({
			alignItems: 'center',
			display: 'flex',
			flex: '0 0 auto',
			background: getStyleProperty('custom-background-light'),
			justifyContent: 'center',
			padding: defaultMuiTheme.spacing(1),
			minHeight: defaultMuiTheme.spacing(7),
			textAlign: 'center',
			'& button': {
				width: '100%',
			},
		}),
		hideScrollbar: buildCSSProperties({
			scrollbarWidth: 'none',
			msOverflowStyle: 'none',
			overscrollBehavior: 'none',
			'&::-webkit-scrollbar': {
				width: 0,
				height: 0,
			},
			webkitOverflowScrolling: 'auto',
		}),
		positionSticky: buildCSSProperties({
			position: isSafari ? '-webkit-sticky' : 'sticky',
		}),
		readOnlyLabel: buildCSSProperties({
			display: 'inline-block',
			fontSize: '0.75rem',
			lineHeight: '1rem',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			width: '100%',
		}),
		scrollable: buildCSSProperties({
			overflow: 'auto',
			WebkitOverflowScrolling: 'touch',
		}),
		scrollableX: buildCSSProperties({
			overflowX: 'auto',
			WebkitOverflowScrolling: 'touch',
			overflowY: 'hidden',
		}),
		scrollableY: buildCSSProperties({
			overflowY: 'auto',
			WebkitOverflowScrolling: 'touch',
			overflowX: 'hidden',
		}),
		// TODO: utilize container
		// https://material-ui.com/components/container/
		settingsContainer: buildCSSProperties({
			maxWidth: 1000,
		}),
		forceScrollable: buildCSSProperties({
			overflow: 'auto !important',
			WebkitOverflowScrolling: 'touch !important' as any,
		}),
		watermark: (
			offset?: Partial<{
				left?: number;
				top: number;
				right: number;
				bottom: number;
			}>,
		): CSSProperties => {
			const watermarkPosition: WatermarkPosition = (getCurrentPortalProp(
				'watermarkPos',
			) ?? 'center') as WatermarkPosition;
			let position: Record<string, number | string> = {};
			switch (watermarkPosition) {
				case 'top-left':
					position = {
						backgroundPosition: 'top left',
						top: 0 + (offset?.top ?? 0),
						left: 0 + (offset?.left ?? 0),
						maxWidth: '50%',
						maxHeight: '50%',
					};
					break;
				case 'top-right':
					position = {
						backgroundPosition: 'top right',
						top: 0 + (offset?.top ?? 0),
						right: 0 + (offset?.right ?? 0),
						maxWidth: '50%',
						maxHeight: '50%',
					};
					break;
				case 'bottom-left':
					position = {
						backgroundPosition: 'bottom left',
						bottom: 0 + (offset?.bottom ?? 0),
						left: 0 + (offset?.left ?? 0),
						maxWidth: '50%',
						maxHeight: '50%',
					};
					break;
				case 'bottom-right':
					position = {
						backgroundPosition: 'bottom right',
						bottom: 0 + (offset?.bottom ?? 0),
						right: 0 + (offset?.right ?? 0),
						maxWidth: '50%',
						maxHeight: '50%',
					};
					break;
				default:
					position = {
						top: 0 + (offset?.top ?? 0),
						left: 0 + (offset?.left ?? 0),
						bottom: 0 + (offset?.bottom ?? 0),
						right: 0 + (offset?.right ?? 0),
					};
					break;
			}
			return {
				backgroundImage: `url(${URL.watermark}?cb=${watermarkCacheBuster})`,
				backgroundPosition: 'center',
				backgroundSize: 'contain',
				backgroundRepeat: 'no-repeat',
				opacity: 0.3,
				pointerEvents: 'none',
				position: 'absolute',
				width: '100%',
				height: '100%',
				...position,
			};
		},
	};
};
